/*
 * Server-side quiz answer storage with a localStorage write-behind retry
 * buffer. Used by form-assessment.ts on dynamically-rendered quiz pages;
 * legacy baked quiz pages still save answers.json to GitHub via login.ts.
 *
 * Endpoints (session-cookie auth, same origin):
 *
 *   GET /api/quiz-answers/:courseId/:kind/:name
 *     200 { quizId, answers: { [uuid]: { answer, savedAt } } }
 *     401 { anonymous: true, family: boolean } when not logged in.
 *
 *   PUT /api/quiz-answers/:courseId/:kind/:name/:uuid  body { answer }
 *     200 { ok: true, savedAt }; 401 anonymous; 403 quiz not visible/closed;
 *     404 unknown question.
 *
 *   GET /api/quiz-key/:courseId/:kind/:name
 *     200 { key: { [uuid]: { answer, explanation } } } when released;
 *     403 { released: false } otherwise.
 *
 * Every save is written to memory and localStorage first and then PUT to the
 * server; saves that can't land (offline, 5xx) stay buffered and are retried
 * on an interval with backoff, on the browser's `online` event, before any
 * subsequent save, and at the start of the next visit.
 *
 * The terminal-drop rule (plans/surface-api-errors.md D4): a buffered answer
 * is dropped only when the server has authoritatively rejected THAT content
 * — today just 403, quiz closed/not visible — and even then the question
 * shows a persistent state. Anything unexpected (a 404 for an unknown
 * question included: mid-deploy, or a quiz-config mistake about to be fixed
 * — the u1l3.5 checkpoint incident) keeps the buffer, shows an `error`
 * state, and retries on the slow schedule, so the answers land once the
 * server is fixed instead of vanishing behind a page that looks fine.
 */

import { apiError, apiFetch, errorDetail } from './fetch-helpers.ts';

export type ChoiceAnswer = { index: number; text: string };

// A string for the free forms, a ChoiceAnswer for choices, ChoiceAnswer[]
// for mchoices, string[] for a multianswer's list.
export type AnswerValue = string | string[] | ChoiceAnswer | ChoiceAnswer[];

// 'unsaved' is the auth-lost case: the save can't land until the student
// logs back in (the buffer keeps it for a future logged-in visit). 'error'
// is the server refusing the save for an unexpected reason (4xx other than
// 401/403): still buffered, still retrying, but the student must see it.
export type SaveState = 'saving' | 'saved' | 'offline' | 'closed' | 'unsaved' | 'error';

export interface AuthInfo {
  anonymous: boolean;
  family: boolean;
}

export interface KeyEntry {
  answer: AnswerValue;
  explanation?: string;
  // A rubric question's key (plans/rubric-questions.md): the items a
  // complete answer includes, in place of accepted answers.
  rubric?: { html: string; points: number }[];
}

export interface StoreCallbacks {
  // `detail` accompanies the 'error' state: the server's own diagnosis.
  onSaveState: (uuid: string, state: SaveState, savedAt?: string, detail?: string) => void;
  onAuthLost: (auth: AuthInfo) => void;
  // The initial load of our saved answers failed for a reason other than
  // being logged out (the page still works and new answers still save).
  onLoadError?: (message: string) => void;
}

interface PendingSave {
  answer: AnswerValue;
  queuedAt: string;
}

const RETRY_MIN_MS = 5 * 1000;
const RETRY_MAX_MS = 5 * 60 * 1000;
// An answer the server refused with an unexpected 4xx is retried no more
// often than this (it's most likely a config problem, not a blip).
const RETRY_ERROR_MS = 60 * 1000;

class QuizAnswerStore {
  // Latest known answer per question uuid (server values overlaid with any
  // local saves, buffered or not, which are always at least as new).
  answers: Record<string, AnswerValue> = {};

  private answersURL: string;
  private keyURL: string;
  private bufferKey: string;
  private callbacks: StoreCallbacks;
  private pending: Record<string, PendingSave> = {};
  // When each errored ('error' state) answer last failed, for the slow retry.
  private errored: Record<string, number> = {};
  private flushing = false;
  private loggedOut = false;
  private retryMs = RETRY_MIN_MS;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(course: string, kind: string, name: string, callbacks: StoreCallbacks) {
    const path = [course, kind, name].map(encodeURIComponent).join('/');
    this.answersURL = `/api/quiz-answers/${path}`;
    this.keyURL = `/api/quiz-key/${path}`;
    this.bufferKey = `quiz:${course}:${kind}:${name}`;
    this.callbacks = callbacks;
    window.addEventListener('online', () => this.flush());
  }

  /*
   * Load state: flush any saves buffered by a previous visit, then fetch the
   * server's copy of our answers. Buffered local values win over the server's
   * (they are newer saves that haven't landed yet). Returns 'ok' when logged
   * in; otherwise who we appear to be, so the page can explain that answers
   * aren't being saved. Either way `answers` is filled in as best we can.
   */
  async init(): Promise<'ok' | AuthInfo> {
    this.pending = this.readBuffer();
    for (const [uuid, save] of Object.entries(this.pending)) {
      this.answers[uuid] = save.answer;
    }
    await this.flush();
    if (this.loggedOut) {
      return this.authInfo;
    }
    try {
      const r = await apiFetch(this.answersURL);
      if (r.status === 401) {
        this.logOut(await r.json().catch(() => ({})));
        return this.authInfo;
      }
      if (r.ok) {
        const data: { answers?: Record<string, { answer: AnswerValue }> } = await r.json();
        for (const [uuid, entry] of Object.entries(data.answers ?? {})) {
          if (!(uuid in this.pending)) {
            this.answers[uuid] = entry.answer;
          }
        }
      } else {
        throw await apiError(r);
      }
    } catch (e) {
      // Offline or a server error: buffered answers already fill the UI and
      // will retry, but the student must know their earlier answers aren't
      // showing.
      console.error('Could not load saved answers.', e);
      this.callbacks.onLoadError?.(errorDetail(e));
    }
    return 'ok';
  }

  /*
   * Record a new answer. Returns true if the save was queued for the server,
   * false if we know we're not logged in and it can't land now. Either way
   * the answer goes in the localStorage buffer, so work done while logged
   * out still flushes on the next logged-in visit.
   */
  save(uuid: string, answer: AnswerValue): boolean {
    this.answers[uuid] = answer;
    this.pending[uuid] = { answer, queuedAt: new Date().toISOString() };
    delete this.errored[uuid]; // new content: try it right away
    this.writeBuffer();
    if (this.loggedOut) {
      return false;
    }
    this.retryMs = RETRY_MIN_MS; // User activity resets the retry backoff.
    this.flush();
    return true;
  }

  isPending(uuid: string): boolean {
    return uuid in this.pending;
  }

  /*
   * Fetch the answer key. Returns null if it's not released yet (403), we're
   * not logged in, or we're offline — all handled the same way: no key.
   */
  async fetchKey(): Promise<Record<string, KeyEntry> | null> {
    try {
      const r = await apiFetch(this.keyURL);
      if (!r.ok) return null;
      return (await r.json()).key ?? null;
    } catch {
      return null;
    }
  }

  private get authInfo(): AuthInfo {
    return { anonymous: true, family: this.family };
  }

  private family = false;

  private logOut(body: { family?: unknown } | null | undefined) {
    this.loggedOut = true;
    this.family = !!body?.family;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private async flush() {
    if (this.flushing || this.loggedOut) return;
    this.flushing = true;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    try {
      for (const uuid of Object.keys(this.pending)) {
        const save = this.pending[uuid];
        if (!save) continue;
        const erroredAt = this.errored[uuid];
        if (erroredAt !== undefined && Date.now() - erroredAt < RETRY_ERROR_MS) continue;
        this.callbacks.onSaveState(uuid, 'saving');

        let r: Response;
        try {
          r = await apiFetch(`${this.answersURL}/${encodeURIComponent(uuid)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ answer: save.answer }),
          });
        } catch {
          this.callbacks.onSaveState(uuid, 'offline');
          this.scheduleRetry();
          return;
        }

        if (r.ok) {
          const { savedAt }: { savedAt?: string } = await r.json().catch(() => ({}));
          if (this.pending[uuid] === save) {
            // Not superseded by a newer save while this one was in flight.
            delete this.pending[uuid];
            this.writeBuffer();
          }
          delete this.errored[uuid];
          this.retryMs = RETRY_MIN_MS;
          this.callbacks.onSaveState(uuid, 'saved', savedAt);
        } else if (r.status === 401) {
          // Session gone. Stop saving but leave the buffer alone: it will
          // flush on a future logged-in visit, so nothing is lost. Every
          // still-pending answer (this one included) gets marked unsaved so
          // no question is left showing a stuck "Saving…".
          this.logOut(await r.json().catch(() => ({})));
          for (const pendingUuid of Object.keys(this.pending)) {
            this.callbacks.onSaveState(pendingUuid, 'unsaved');
          }
          this.callbacks.onAuthLost(this.authInfo);
          return;
        } else if (r.status === 403) {
          // Quiz closed or not visible: the server has rejected this content
          // for good; retrying won't help. The one sanctioned drop.
          delete this.pending[uuid];
          this.writeBuffer();
          this.callbacks.onSaveState(uuid, 'closed');
        } else if (r.status >= 500) {
          this.callbacks.onSaveState(uuid, 'offline');
          this.scheduleRetry();
          return;
        } else {
          // 404 unknown question or another client error: NOT dropped. Keep
          // the buffer, show the server's reason, and keep retrying slowly
          // — a config fix on the server lands these answers after the fact.
          const err = await apiError(r);
          console.error(`Answer for ${uuid} refused (${errorDetail(err)}); keeping it buffered`);
          this.errored[uuid] = Date.now();
          this.callbacks.onSaveState(uuid, 'error', undefined, errorDetail(err));
        }
      }
    } finally {
      this.flushing = false;
    }
    // Anything queued while we were flushing gets picked up on the next pass.
    if (Object.keys(this.pending).length > 0) {
      this.scheduleRetry();
    }
  }

  private scheduleRetry() {
    if (this.timer || this.loggedOut) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.flush();
    }, this.retryMs);
    this.retryMs = Math.min(this.retryMs * 2, RETRY_MAX_MS);
  }

  private readBuffer(): Record<string, PendingSave> {
    try {
      const raw = localStorage.getItem(this.bufferKey);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private writeBuffer() {
    try {
      if (Object.keys(this.pending).length === 0) {
        localStorage.removeItem(this.bufferKey);
      } else {
        localStorage.setItem(this.bufferKey, JSON.stringify(this.pending));
      }
    } catch {
      // localStorage unavailable: the retry buffer is memory-only this visit.
    }
  }
}

export { QuizAnswerStore };
